import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import gql from 'graphql-tag';

import React, {useEffect, useState} from 'react';
import {useSnackbar} from 'notistack';
import Loading from '../Loading/Loading';
import {Panel} from '../Panel/Panel';
import {useAccountContext} from '../../contexts/AccountContext';
import {
  AllXperidocTemplates,
  AllXperidocTemplatesVariables,
} from '../../schema-types/AllXperidocTemplates';
import {
  CreateXperidocExecution,
  CreateXperidocExecutionVariables,
} from '../../schema-types/CreateXperidocExecution';
import {
  GetXperidocExecution,
  GetXperidocExecutionVariables,
} from '../../schema-types/GetXperidocExecution';
import {themeColors} from '../../theme/Theme';
import {newUTCDate} from '../../utils/newUTCDate';

const createXperidocExecutionMutation = gql`
  mutation CreateXperidocExecution(
    $entityId: String!
    $flowId: String!
    $entityType: String!
  ) {
    createXperidocExecution(
      entity: $entityType
      entityId: $entityId
      flowId: $flowId
      # the name parameter is actually required in the api contract
      name: "test"
    ) {
      xperidocExecution {
        id
      }
      ok
    }
  }
`;

const getXperidocExecution = gql`
  query GetXperidocExecution($id: UUID!) {
    xperidocExecutions(id: $id, refreshLinks: true) {
      id
      dateCreated
      name
      status
      documents {
        label
        link
      }
    }
  }
`;

const allXperidocTemplates = gql`
  query AllXperidocTemplates($entity: String!, $forceSync: Boolean) {
    xperidocTemplates(entity: $entity, tags: ["oneview-enabled"], forceSync: $forceSync) {
      id
      flowId
      entity
      name
    }
  }
`;

export type XperidocTemplate = 'account' | 'contact' | 't4a_review';

export const DocumentGenerator: React.FC<{
  entity: XperidocTemplate;
  entityId?: string;
}> = ({entity, entityId}) => {
  const {accountType} = useAccountContext();
  return (
    <Grid item xs={12}>
      <Panel color={themeColors.lightPink} title="Generate documents">
        <Box mb={4}>
          <DocumentModalInner entityId={entityId} entity={entity} />
        </Box>
      </Panel>
    </Grid>
  );
};
export const DocumentModalInner: React.FC<{
  entity: XperidocTemplate;
  entityId?: string;
  disabled?: boolean;
}> = ({entity, entityId, disabled}) => {
  const {account} = useAccountContext();
  const [
    queryAllXperidocTemplates,
    {data: templatesData, loading: templatesLoading, error},
  ] = useLazyQuery<AllXperidocTemplates, AllXperidocTemplatesVariables>(
    allXperidocTemplates
  );

  const [executionId, setExecutionId] = useState<string | null>(null);
  const {enqueueSnackbar} = useSnackbar();
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(null);
  const [onCreateDocument, {data, loading: createLoading, called: createCalled}] =
    useMutation<CreateXperidocExecution, CreateXperidocExecutionVariables>(
      createXperidocExecutionMutation
    );
  const [isForceSync, setIsForceSync] = useState(false);

  useEffect(() => {
    if (templatesData?.xperidocTemplates || isForceSync) {
      return;
    }
    queryAllXperidocTemplates({
      variables: {
        entity,
        forceSync: false,
      },
    });
  }, [templatesData, isForceSync]);

  if (templatesLoading) {
    return <Loading />;
  }

  if (!templatesData?.xperidocTemplates || !account?.curoId) {
    return <Alert severity="error">{error?.message || 'Document template error'}</Alert>;
  }
  return (
    <>
      <Box mt={4} mb={2} flexDirection="column">
        <FormControl>
          <InputLabel id="goalSelect">Select Document</InputLabel>
          <Select
            disabled={disabled}
            style={{minWidth: 300}}
            variant="outlined"
            label="Select Document"
            inputProps={{
              'data-select-input': 'Select Template',
            }}
            data-testid="Select Template"
            value={selectedTemplateId}
            onChange={(e) => setSelectedTemplateId(e.target.value as string)}
          >
            {templatesData?.xperidocTemplates
              ?.filter((option) => {
                return !!option;
              })
              .map((option) => {
                if (!option || !option.flowId) {
                  return false;
                }
                return (
                  <MenuItem
                    data-test={option.name}
                    key={option.flowId}
                    value={option.flowId}
                  >
                    {option.name}
                  </MenuItem>
                );
              })}
          </Select>
        </FormControl>
      </Box>

      <Box display="flex" flexDirection="row">
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedTemplateId || disabled}
          onClick={() =>
            onCreateDocument({
              variables: {
                entityType: entity,
                entityId: entityId || account!.curoId!,
                flowId: selectedTemplateId!,
              },
            }).then(({data: outcomeData}) => {
              if (outcomeData?.createXperidocExecution?.xperidocExecution?.id) {
                setExecutionId(outcomeData.createXperidocExecution.xperidocExecution.id);
              }
            })
          }
        >
          Create document
        </Button>
        <Box ml={1}>
          <Button
            variant="outlined"
            disabled={disabled}
            color="primary"
            onClick={async () => {
              setIsForceSync(true);
              await queryAllXperidocTemplates({
                variables: {
                  entity,
                  forceSync: true,
                },
                onCompleted: () => setIsForceSync(false),
                onError: (err) => {
                  enqueueSnackbar(`${err.message}`, {
                    variant: 'error',
                    autoHideDuration: null,
                  });
                },
              });
            }}
          >
            Sync Templates
          </Button>
        </Box>
      </Box>

      {data && !data?.createXperidocExecution?.ok ? (
        <div>Unable to create documents</div>
      ) : null}
      {createLoading ? <Loading /> : <DocumentList executionId={executionId} />}
    </>
  );
};

export const DocumentList: React.FC<{executionId?: string | null}> = ({executionId}) => {
  const {data: documentsData, loading: getDocumentsLoading} = useQuery<
    GetXperidocExecution,
    GetXperidocExecutionVariables
  >(getXperidocExecution, {
    variables: {id: executionId!},
    skip: !executionId,
    pollInterval: 10000,
  });
  if (!executionId) {
    return null;
  }
  if (getDocumentsLoading) {
    return <Loading />;
  }
  return (
    <Box key={executionId} ml={1} mt={5}>
      <Box mt={3}>
        <Grid container spacing={2}>
          {documentsData?.xperidocExecutions?.map((exec) => {
            if (!exec) {
              return null;
            }
            if (exec.status === 'queued' || exec.status === 'running') {
              return (
                <Typography key={exec.id} variant="h6">
                  Creating documents, this may take 2-3 minutes. Please keep this window
                  open.
                </Typography>
              );
            }
            if (exec.status === 'error') {
              return (
                <Typography key={exec.id} color="error" variant="h6">
                  Error creating documents
                </Typography>
              );
            }

            return (
              <>
                <Typography variant="h6">Documents:</Typography>

                {exec.documents?.map((doc) => {
                  if (!doc || !doc.link) {
                    return null;
                  }
                  return (
                    <Grid xs={12} item key={doc.link}>
                      <a
                        style={{color: 'inherit'}}
                        key={doc.label}
                        href={doc.link}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {doc?.label} (created{' '}
                        {newUTCDate(exec.dateCreated).toLocaleTimeString()})
                      </a>
                    </Grid>
                  );
                })}
              </>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
};
